import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { useFetchProducts } from '@/data/swrs/shopify';
import { VideoContext } from '@/Pages/VideoPlayer/VideoContext';

// Components
import Layout from '@/Layout';
import VideoSection from '@/v2/pages/AllVideos/components/VideoSection';
import FilterSection from '@/v2/pages/AllVideos/components/FilterSection';
import MobileFilterSection from '@/v2/pages/AllVideos/components/MobileFilterSection';
import AddToPlayListModal from '@/component/playlist/personal-playlist/AddToPlayListModal';

// Hooks
import { useVideoData } from '@/v2/pages/AllVideos/hooks/useVideoData';
import { useSavedVideos } from '@/v2/pages/AllVideos/hooks/useSavedVideos';
import { useAuth } from '@/v2/hooks/useAuth';
import { getUniqueTagsFromVideos } from '@/utils/getUniqueTagsFromVideos';

export const AllVideos = () => {
	const { search } = useLocation();
	const query = new URLSearchParams(search);
	const tagData = query.get('tag');
	const navigate = useNavigate();

	// Context and global state
	const { selectedVideo, setSelectedVideo } = useContext(VideoContext);
	const userData = useAtomValue(userInfoAtom);
	const setUserData = useSetAtom(userInfoAtom);
	const { data: productsData } = useFetchProducts();

	// Filter state
	const [selectedTags, setSelectedTags] = useState([]);
	const [selectedDurations, setSelectedDurations] = useState([]);
	const [selectedLevels, setselectedLevels] = useState([]);
	const [selectedInstructors, setSelectedInstructors] = useState([]);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [sortby, setSortBy] = useState('latest');
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 15;
	const [startIndex, setStartIndex] = useState(0);

	// Playlist state
	const [modal, setModal] = useState(false);
	const [videoData, setVideoData] = useState({});
	const [videoData2, setVideoData2] = useState({});

	// Custom hooks
	const { isLoggedIn } = useAuth();
	const { savedVideoIds, setSavedVideoIds } = useSavedVideos(userData);
	const { responseData, setResponseData, totvideos, fetchData, tags, instructors, products, setProducts, setInstructors } =
		useVideoData({
			userInfo: userData.user,
			startIndex,
			itemsPerPage,
			userData,
			selectedTags,
			selectedDurations,
			selectedLevels,
			selectedInstructors,
			selectedProducts,
			sortby
		});

	// Initialize products when data is available
	useEffect(() => {
		if (productsData?.products) {
			const formattedProducts = productsData.products.map(product => ({
				name: product?.title,
				id: product?.id,
				subname: product?.metafieldValue || ''
			}));
			setProducts(formattedProducts);
		}
	}, [productsData]);

	// Handle tag from URL
	useEffect(() => {
		if (tagData) {
			setSelectedTags([tagData]);
		}
	}, [tagData]);

	// Load selected video from localStorage
	useEffect(() => {
		const data = window.localStorage.getItem('selectedVideo');
		if (data) {
			setVideoData(JSON.parse(data));
		}
	}, []);

	// Update video data when selected video changes
	useEffect(() => {
		if (selectedVideo) {
			setVideoData(selectedVideo);
		}
	}, [selectedVideo]);

	// Handle page change
	const handlePageChange = newPage => {
		window.scrollTo(0, 0);
		if (newPage <= 0) {
			newPage = 1;
		}
		setCurrentPage(newPage);
		const newStartIndex = (newPage - 1) * itemsPerPage;
		setStartIndex(newStartIndex);
	};

	// Handle filter change
	const handleFilterChange = event => {
		setSortBy(event.target.value);
	};

	// Filter modal handlers
	const handleOpenFilterModal = () => setIsFilterModalOpen(true);
	const handleCloseFilterModal = () => setIsFilterModalOpen(false);

	// Apply filter
	const handleApplyFilter = (tags, durations, levels, instructors, products) => {
		setSelectedTags(tags);
		setselectedLevels(levels);
		setSelectedDurations(durations);
		setSelectedInstructors(instructors);
		setSelectedProducts(products);
		setIsFilterModalOpen(false);
	};

	// Reset filter
	const handleResetFilter = () => {
		setSelectedTags([]);
		setSelectedDurations([]);
		setselectedLevels([]);
		setSelectedInstructors([]);
		setSelectedProducts([]);
	};

	// Handle shuffle
	const handleShuffle = categoryId => {
		setResponseData(prevData => {
			const shuffledCategory = prevData.find(category => category._id === categoryId);

			if (shuffledCategory) {
				shuffledCategory.videos = shuffleArray(shuffledCategory.videos);
			}

			return [...prevData];
		});
	};

	const shuffleArray = array => {
		const shuffledArray = [...array];
		for (let i = shuffledArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
		}
		return shuffledArray;
	};

	useEffect(() => {
		if (responseData?.length > 0 && instructors.length === 0) {
			setInstructors([
				...new Set(
					responseData[0]?.videos
						.filter(video => video.instructor !== undefined && video.instructor !== null)
						.map(video => video.instructor)
				)
			]);
		}
	}, [responseData, instructors.length]);

	const initialFocusRef = useRef([]);
	
	const uniqueTags = getUniqueTagsFromVideos(responseData[0]?.videos, initialFocusRef);

	return (
		<Layout title="All Videos" description="All Videos of The Godfrey Method - On Demand">
			{/* Mobile Filter Section */}
			<MobileFilterSection
				sortby={sortby}
				handleFilterChange={handleFilterChange}
				handleOpenFilterModal={handleOpenFilterModal}
				isFilterModalOpen={isFilterModalOpen}
				tags={tags}
				selectedTags={selectedTags}
				selectedDurations={selectedDurations}
				selectedLevels={selectedLevels}
				selectedInstructors={selectedInstructors}
				handleTagChange={tag =>
					setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]))
				}
				handleApplyFilter={handleApplyFilter}
				handleResetFilter={handleResetFilter}
				handleCloseFilterModal={handleCloseFilterModal}
				instructors={instructors}
				products={products}
				selectedProducts={selectedProducts}
			/>

			<div>
				<div className="tab-content fade-in" style={{ minHeight: '100vh' }}>
					<div id="menu2" className="tab-pane fade active show">
						{responseData?.map((category, index) => (
							<div key={index} id={category?.name?.split(' ')[0]}>
								{/* Desktop Filter Section */}
								<FilterSection
									category={category}
									sortby={sortby}
									handleFilterChange={handleFilterChange}
									handleOpenFilterModal={handleOpenFilterModal}
									isFilterModalOpen={isFilterModalOpen}
									tags={tags}
									selectedTags={selectedTags}
									selectedDurations={selectedDurations}
									selectedLevels={selectedLevels}
									selectedInstructors={selectedInstructors}
									handleTagChange={tag =>
										setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]))
									}
									handleApplyFilter={handleApplyFilter}
									handleResetFilter={handleResetFilter}
									handleCloseFilterModal={handleCloseFilterModal}
									instructors={instructors}
									products={products}
									selectedProducts={selectedProducts}
									handleShuffle={handleShuffle}
								/>
								{/* Video Section */}
								<VideoSection
									category={category}
									isLoggedIn={isLoggedIn}
									userData={userData}
									setUserData={setUserData}
									savedVideoIds={savedVideoIds}
									setSavedVideoIds={setSavedVideoIds}
									responseData={responseData}
									setResponseData={setResponseData}
									selectedTags={selectedTags}
									selectTagCallback={tag => {
										setSelectedTags(tags => {
											if (tags.includes(tag._id)) {
												return tags.filter(t => t !== tag._id);
											}
											return [...tags, tag._id];
										});
									}}
									setSelectedTags={setSelectedTags}
									setModal={setModal}
									setVideoData2={setVideoData2}
									handleShuffle={handleShuffle}
									totvideos={totvideos}
									currentPage={currentPage}
									handlePageChange={handlePageChange}
									modal={modal}
									setVideoData={setVideoData2}
								/>
							</div>
						))}
					</div>
				</div>
				<AddToPlayListModal modal={modal} setModal={setModal} activeObject={videoData2} viewData={null} />
			</div>
		</Layout>
	);
};
